import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

const MS_CLIENT_ID = (import.meta.env.VITE_MS_CLIENT_ID || "").trim();
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL || "").trim();

export function OutlookSettings() {
  const [connection, setConnection] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [currentSalesId, setCurrentSalesId] = useState<number | null>(null);

  useEffect(() => {
    loadConnection();
  }, []);

  async function loadConnection() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;
    const { data: sale } = await supabase
      .from("sales")
      .select("id")
      .eq("user_id", user.id)
      .single();
    if (!sale) return;
    setCurrentSalesId(sale.id);

    const { data: token } = await supabase
      .from("oauth_tokens")
      .select("*")
      .eq("sales_id", sale.id)
      .eq("provider", "microsoft")
      .single();

    // Only show as connected if we have a real token (not a nonce)
    if (token && !token.access_token.startsWith("nonce:")) {
      setConnection(token);
    }
    setLoading(false);
  }

  async function connectOutlook() {
    if (!currentSalesId || !MS_CLIENT_ID) return;

    // Generate a cryptographic nonce for state parameter
    const nonce = crypto.randomUUID();

    // Store nonce in DB so the callback can verify it
    // Use upsert to handle reconnection (replace existing token)
    const futureDate = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 min expiry
    await supabase.from("oauth_tokens").upsert(
      {
        sales_id: currentSalesId,
        provider: "microsoft",
        access_token: `nonce:${nonce}`,
        refresh_token: "pending",
        token_expires_at: futureDate,
        email: "",
      },
      { onConflict: "sales_id,provider" },
    );

    const redirectUri = `${SUPABASE_URL}/functions/v1/outlook-callback`;
    const scope =
      "https://graph.microsoft.com/Mail.Read offline_access User.Read";
    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${MS_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${nonce}&response_mode=query&prompt=consent`;
    window.location.href = authUrl;
  }

  async function disconnectOutlook() {
    if (!currentSalesId) return;
    await supabase
      .from("oauth_tokens")
      .delete()
      .eq("sales_id", currentSalesId)
      .eq("provider", "microsoft");
    setConnection(null);
    setSyncResult(null);
  }

  async function syncNow() {
    if (!currentSalesId) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("sync-emails", {
        body: { sales_id: currentSalesId },
      });
      if (error) throw new Error(error.message);
      setSyncResult(data);
    } catch (err: any) {
      setSyncResult({ error: err.message });
    }
    setSyncing(false);
  }

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="mb-6 text-2xl font-bold">Outlook Integration</h1>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Microsoft Outlook</CardTitle>
              <CardDescription>
                Sync emails to/from your CRM contacts
              </CardDescription>
            </div>
            {connection ? (
              <Badge className="bg-green-100 text-green-800">Connected</Badge>
            ) : (
              <Badge variant="outline">Not Connected</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {connection ? (
            <>
              <p className="text-sm text-muted-foreground">
                Connected as <strong>{connection.email}</strong>
              </p>
              <div className="flex gap-2">
                <Button onClick={syncNow} disabled={syncing}>
                  {syncing ? "Syncing..." : "Sync Now"}
                </Button>
                <Button variant="outline" onClick={disconnectOutlook}>
                  Disconnect
                </Button>
              </div>
              {syncResult && (
                <div className="rounded-md border p-3 text-sm">
                  {syncResult.error ? (
                    <p className="text-destructive">{syncResult.error}</p>
                  ) : (
                    <p className="text-green-700">
                      Synced {syncResult.synced} emails
                      {syncResult.autoCreated > 0 &&
                        ` (${syncResult.autoCreated} new contacts discovered)`}{" "}
                      ({syncResult.skipped} skipped, {syncResult.total} checked)
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Connect your Microsoft Outlook account to automatically capture
                emails with your CRM contacts. All team members can connect
                independently.
              </p>
              {MS_CLIENT_ID ? (
                <Button onClick={connectOutlook}>Connect Outlook</Button>
              ) : (
                <p className="text-sm text-orange-600">
                  Outlook integration requires Azure AD configuration. Set
                  VITE_MS_CLIENT_ID environment variable and redeploy.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            1. Click "Connect Outlook" to authorize read access to your email
          </p>
          <p>
            2. Click "Sync Now" to pull the last 7 days of emails from both
            Inbox and Sent
          </p>
          <p>
            3. Emails matched to existing contacts are linked; unmatched senders
            are auto-created as "Email Discovered" contacts for review
          </p>
          <p>
            4. All team members' emails appear on the contact detail page — full
            visibility
          </p>
          <p>
            5. Click "Open in Outlook" on any email to view the full message
          </p>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Admin Setup Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong>1.</strong> Go to{" "}
            <a
              href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
              target="_blank"
              rel="noopener"
              className="text-primary underline"
            >
              Azure Portal &rarr; App Registrations
            </a>
          </p>
          <p>
            <strong>2.</strong> Create a new registration (name: "MYLES CRM",
            multi-tenant)
          </p>
          <p>
            <strong>3.</strong> Set redirect URI (Web) to:{" "}
            <code className="rounded bg-muted px-1 text-xs">
              {SUPABASE_URL}/functions/v1/outlook-callback
            </code>
          </p>
          <p>
            <strong>4.</strong> API Permissions → Add: Microsoft Graph →
            Delegated → Mail.Read, User.Read
          </p>
          <p>
            <strong>5.</strong> Certificates & Secrets → New client secret →
            copy value
          </p>
          <p>
            <strong>6.</strong> Set Supabase secrets: MS_CLIENT_ID,
            MS_CLIENT_SECRET, MS_TENANT_ID, APP_URL
          </p>
          <p>
            <strong>7.</strong> Set Vercel env: VITE_MS_CLIENT_ID → redeploy
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
